/**
 * profilePhoto.ts — 올린 사진을 프로필 사진으로 자동 가공(12 설계 §8-D D-1, §14 Q10).
 *
 *   가운데 정사각 자르기 → 128px 축소 → WebP(안 되면 JPEG) → 품질을 낮춰 가며 data URL ≤ 48,000자.
 *   사진 회전 정보(EXIF orientation)는 createImageBitmap({ imageOrientation:"from-image" }) 로 반영하고,
 *   지원하지 않는 브라우저는 <img> 디코딩(브라우저 기본이 회전 반영)으로 대신한다.
 * Storage 를 쓰지 않아 문서에 직접 저장한다(사용자 결정). 사용자가 크기를 맞출 필요 없음.
 */
import { PROFILE_PHOTO_MAX_CHARS, PROFILE_PHOTO_SIZE } from "@/domain/user";

const QUALITIES = [0.86, 0.78, 0.7, 0.6, 0.5, 0.4, 0.3];
/** 128px 로도 넘으면 더 작게 */
const FALLBACK_SIZES = [PROFILE_PHOTO_SIZE, 112, 96];
/** 원본 파일 상한 — 너무 큰 파일은 디코딩 전에 거른다 */
const MAX_FILE_BYTES = 20 * 1024 * 1024;

type Drawable = ImageBitmap | HTMLImageElement;

function sizeOf(src: Drawable): { w: number; h: number } {
  if ("naturalWidth" in src) return { w: src.naturalWidth, h: src.naturalHeight };
  return { w: src.width, h: src.height };
}

async function decode(file: File): Promise<{ img: Drawable; release: () => void }> {
  if (typeof createImageBitmap === "function") {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
      return { img: bmp, release: () => bmp.close() };
    } catch {
      /* HEIC 등 — 아래 <img> 로 다시 시도 */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    return { img, release: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

function render(img: Drawable, size: number, type: string, quality: number): string {
  const { w, h } = sizeOf(img);
  const side = Math.min(w, h);
  const sx = (w - side) / 2;
  const sy = (h - side) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("이미지를 처리할 수 없는 브라우저입니다.");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
  return canvas.toDataURL(type, quality);
}

/** 파일 → 프로필 사진 data URL. 실패하면 사용자에게 보일 문구로 throw */
export async function makeProfilePhoto(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) throw new Error("이미지 파일을 선택하세요.");
  if (file.size > MAX_FILE_BYTES) throw new Error("파일이 너무 큽니다(20MB 이하).");
  let decoded: { img: Drawable; release: () => void };
  try {
    decoded = await decode(file);
  } catch {
    throw new Error("이 사진 형식은 읽을 수 없습니다. JPG·PNG 사진으로 다시 시도하세요.");
  }
  try {
    const { w, h } = sizeOf(decoded.img);
    if (!w || !h) throw new Error("이미지 크기를 읽을 수 없습니다.");
    // WebP 인코딩을 지원하는지 — 안 되면 toDataURL 이 PNG 를 돌려준다
    const webpOk = render(decoded.img, 8, "image/webp", 0.8).startsWith("data:image/webp");
    const type = webpOk ? "image/webp" : "image/jpeg";
    for (const size of FALLBACK_SIZES) {
      for (const q of QUALITIES) {
        const url = render(decoded.img, size, type, q);
        if (url.length <= PROFILE_PHOTO_MAX_CHARS) return url;
      }
    }
    throw new Error("사진을 충분히 작게 줄이지 못했습니다. 다른 사진으로 시도하세요.");
  } finally {
    decoded.release();
  }
}
